<template>
	<div v-if="hasHover" class="base-image-multiple">
		<img
			:src="imageSrc"
			class="base-image"
			:class="additionalClasses"
			v-bind="{ alt, ...responsiveImage }"
		/>
		<img
			:src="hoverImageSrc ?? ''"
			class="base-image base-image--hover"
			:class="additionalClasses"
			v-bind="{ alt, ...hoverResponsiveImage }"
		/>
	</div>
	<img
		v-else
		:src="imageSrc"
		class="base-image"
		:class="additionalClasses"
		v-bind="{ alt, ...responsiveImage }"
	/>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import { ImageSetModel } from '@/store/models/Image';

export default defineComponent({
	props: {
		images: {
			type: [Object as () => ImageSetModel, Array as () => Array<ImageSetModel>],
			required: true,
		},

		activeImage: {
			type: Number,
			default: 0,
		},

		alt: {
			type: String,
			default: '',
		},

		customMode: {
			type: String,
			default: '',
		},
	},

	computed: {
		imageObject(): ImageSetModel {
			return Array.isArray(this.images) ? this.images[this.activeImage] : this.images;
		},

		imageSrc(): string {
			return this.imageObject['image_1x'];
		},

		hoverImageSrc(): string | null | undefined {
			return this.imageObject['hover_image_1x'];
		},

		mode(): string | null | undefined {
			return this.customMode || this.imageObject['mode'];
		},

		additionalClasses(): string[] {
			if (!this.mode) return [];

			return [`base-image--${this.mode}`];
		},

		hasHover(): boolean {
			return !!this.hoverImageSrc;
		},

		responsiveImage() {
			return this.getSrcset(false);
		},

		hoverResponsiveImage() {
			return this.getSrcset(true);
		},
	},

	methods: {
		getSrcset(hover = false): { srcset: string; sizes: string } {
			const sizeMap = {
				'1x': 768,
				'2x': 1024,
				'3x': 1280,
			};

			const srcset = Object.entries(sizeMap)
				.map(([_key, size]) => {
					const key = hover
						? (`hover_image_${_key as keyof typeof sizeMap}` as const)
						: (`image_${_key as keyof typeof sizeMap}` as const);

					return this.imageObject[key] ? `${this.imageObject[key]} ${size}w` : null;
				})
				.filter((item): item is string => !!item)
				.join(', ');
			const sizes = Object.values(sizeMap)
				.map((size, index, arr) => {
					return index === arr.length - 1 ? `${size}px` : `(max-width: ${size}px) ${size}px`;
				})
				.join(',');

			return { srcset, sizes };
		},
	},
});
</script>

<style lang="scss">
.base-image {
	width: 100%;
	height: 100%;
	object-fit: contain;
	transition: opacity 0.2s ease;

	&--cover {
		object-fit: cover;
	}

	&--hover {
		opacity: 0;
	}
}

.base-image-multiple {
	width: 100%;
	height: 100%;

	&:hover {
		.base-image {
			opacity: 0;

			&--hover {
				opacity: 1;
			}
		}
	}
}
</style>
